import { yupResolver } from '@hookform/resolvers/yup';
import { Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import { upperCase } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AdditionalServiceModal from 'src/app/MyQuote/Modals/AdditionalServiceModal';
import DangerousGoodsModal from 'src/app/MyQuote/Modals/DangerousGoodsModal';
import InBondModal from 'src/app/MyQuote/Modals/InBondModal';
import LimitedAccessDeliveryModal from 'src/app/MyQuote/Modals/LimitedAccessDeliveryModal';
import LinearFootageMismatchModal from 'src/app/MyQuote/Modals/LinearFootageMismatchModal';
import LoadInsuranceModal from 'src/app/MyQuote/Modals/LoadInsuranceModal';
import { TAILGATE_SERVICE_IDS } from 'src/app/MyQuote/Modals/quoteDetailModal.const';
import { IDangerousGoodsForm, IInBondForm, ILimitedAccessDeliveryData, ILimitedAccessDeliveryDataForm, ILoadInsuranceForm } from 'src/app/MyQuote/Modals/quoteDetailModal.interface';
import WeightLinearFootageMismatchModal from 'src/app/MyQuote/Modals/WeightLinearFootageMismatchModal';
import WeightMismatchModal from 'src/app/MyQuote/Modals/WeightMismatchModal';
import CheckBox from 'src/components/CheckBox';
import ConfirmModal from 'src/components/ConfirmModal';
import InputText from 'src/components/InputText/InputText';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { CURRENCY, DIMENSION_REQUIRED_EQUIPMENT_TYPES, FOOT, KGS_MAX_VALUES, LBS_MAX_VALUES, MEASUREMENT, QUOTE_CLASSIFICATION } from 'src/constants/common';
import { RootState } from 'src/redux/store';
import { calculateLinearFootage, findServiceTypeIdBySlug, formatLinearFootage, getCalculatedFreightClass, collectFormErrorPaths, scrollToFirstFormError } from 'src/utils/CommonFunctions';
import { createDimensionMaxValidator } from 'src/utils/dimensionValidation';
import WalToast from 'src/utils/WalToast';
import * as yup from 'yup';

import { ADD_QUOTE_KEYS } from '../../addQuote.const';
import {
    addQuoteAllData,
    addQuoteCompletedSteps as addQuoteCompletedStepsSelector,
    addQuoteServiceType as addQuoteServiceTypeSelector,
    addQuoteShipmentDetails as addQuoteShipmentDetailsSelector,
    addQuoteStopCount as addQuoteStopCountSelector,
    addQuoteStops as addQuoteStopsSelector,
    isCreateLoading as isCreateLoadingSelector,
} from '../../AddQuote.slice';
import { clearInvalidStopRouting, validateItemRouting } from '../../multiStop.helpers';
import StepperAction from '../StepperAction';
import { useSaveNextStep } from '../useSaveNextStep';
import { useRegisterStepperPersist } from '../useStepperPersist';

import LineItem from './LineItem';

const initialQuoteDetailAction = {
    tailgate: false,
    otherService: false,
    inBond: false,
    loadInsurance: false,
    dangerousGoods: false,
    limitedAccess: false,
    weightMismatch: false,
    linearFootageMismatch: false,
    weightLinearFootageMismatch: false,
    data: null as { [key: string]: any } | null,
};

export interface IDimensions {
    id: number | null;
    handlingUnit: number;
    handlingUnitNo: number;
    weight: number;
    itemLength: number;
    width: number;
    height: number;
    totalWeight: number;
    isStack: boolean;
    commodity: string | null;
    freightClass: number;
    itemClassForDayross: number;
    // Multi-stop route (Task 7) — optional because classic 2-stop quotes never populate these
    // (LineItem only renders the routing selectors when stopCount > 2). See multiStop.helpers.ts.
    pickupStopPosition?: number | null;
    dropStopPosition?: number | null;
}

export interface IAdditionalServices {
    serviceId?: number[];
    dangerousGoods?: boolean;
    dangerGoodsType?: string;
    description?: string;
    emergencyContactExt?: string;
    emergencyContactName?: string;
    emergencyContactNumber?: string;
    goodsClass?: string;
    inbond?: boolean;
    inbondType?: string;
    inbondName?: string;
    inbondAddress?: string;
    inbondContactMethod?: string;
    inbondContactValue?: string;
    unitedNationsNumber?: string;
    emergencyEapnbr?: string;
    limitedAccess?: boolean;
    limitedAccessType?: string;
    limitedAccessOther?: string;
    packagingGroup?: string;
    insuranceCommercialValue?: string;
    insuranceCurrencyType?: string;
    insuranceCommodity?: string;
}

const isSameDimensions = (dims: Array<Record<string, any>>, defaultDims: Array<Record<string, any>>): boolean => {
    if (dims.length !== defaultDims.length) return false;

    return dims.every((dim: Record<string, any>, i: number) => {
        const def = defaultDims[i];

        return Object.keys(def).every((key) => dim[key] === def[key]);
    });
};

export interface IDimensionsForm {
    serviceId: number[];
    dimensions: IDimensions[];
    totalWeight: string | number | null;
    linearFootage: string | number | null;
    weightMeasurement: string;
    lengthMeasurement: string;
    additionalServices: IAdditionalServices;
}

const defaultDimensions = [
    {
        id: null,
        handlingUnit: 1,
        handlingUnitNo: 0,
        weight: 0,
        itemLength: 0,
        width: 0,
        height: 0,
        totalWeight: 0,
        isStack: true,
        commodity: null,
        freightClass: 0,
        itemClassForDayross: 0,
    },
];

interface AddDimensionsProps {
    setHeaderChildren: (children: React.ReactNode) => void;
}

// eslint-disable-next-line max-lines-per-function
const AddDimensions = ({ setHeaderChildren }: AddDimensionsProps) => {
    const { t } = useTranslation();
    const [action, setAction] = useState(initialQuoteDetailAction);
    const { serviceTypes, services } = useSelector((state: RootState) => state.basic);
    const serviceRefName = useRef<HTMLParagraphElement>(null);
    const quoteData = useSelector(addQuoteAllData);
    // AddQuote uses serviceType (string like 'ltl'/'ftl') instead of quoteDetails.classification
    const serviceType = useSelector(addQuoteServiceTypeSelector);
    const shipmentDetails = useSelector(addQuoteShipmentDetailsSelector);
    const completedSteps = useSelector(addQuoteCompletedStepsSelector);
    // Multi-stop route (Task 7) — gates the routing validation below; stays 2 on classic quotes.
    const stopCount = useSelector(addQuoteStopCountSelector);
    // Task 11 (P1.5) — middle-stop activityHint lives here (IStopDetailsData). Task 20 widened its
    // use: buildStopOptions (LineItem.tsx) now hard-filters the routing dropdowns by it, and the
    // effect below auto-clears any item routed through a stop in a direction its (possibly
    // since-changed) hint no longer allows.
    const stops = useSelector(addQuoteStopsSelector);
    const hasInitializedServices = useRef<boolean>(false);
    const { onSaveNext, onBack, onPersist } = useSaveNextStep();
    // classification mirrors the CreateQuote pattern — in AddQuote, serviceType IS the classification
    const classification = useMemo(() => serviceType, [serviceType]);
    const isLoading = useSelector(isCreateLoadingSelector);

    const isDimensionRequired = useMemo(() => {
        // Multi-stop route (Task 8, widened Task 17 per client decision) — ANY multi-stop quote
        // (LTL or FTL) now requires real line items, not just FTL. Originally this only forced
        // true for FTL multileg (classic 2-stop FTL never renders AddDimensions at all — see
        // NewAddQuote.tsx / ADD_QUOTE_STEPS's CP5 note — and the BE's POST /quotes/multi-stop Joi
        // contract requires min(1) line items unconditionally regardless of classification, so an
        // LTL multileg quote with zero/blank line items would 409 against the BE too). Force it
        // here for every `stopCount > 2` quote rather than falling through to the equipment-type
        // check below, which is classic-2-stop-shaped and would let an LTL multileg quote submit
        // with a blank default line item.
        if (stopCount > 2) {
            return true;
        }

        const fullName = shipmentDetails?.primaryEquipmentTypeFullName?.toLowerCase() ?? 'dry van';

        return DIMENSION_REQUIRED_EQUIPMENT_TYPES.some((type) => fullName.includes(type));
    }, [shipmentDetails?.primaryEquipmentTypeFullName, stopCount]);

    const isAllPreviousStepsCompleted = useMemo(() => {
        const requiredSteps = [ADD_QUOTE_KEYS.SHIPPER_DETAILS, ADD_QUOTE_KEYS.CONSIGNEE_DETAILS, ADD_QUOTE_KEYS.SHIPMENT_DETAILS];

        return requiredSteps.every((step) => completedSteps.includes(step));
    }, [completedSteps]);

    const handleClose = useCallback(() => {
        setAction(initialQuoteDetailAction);
    }, []);

    const isDimensionFieldRequired = function (this: yup.TestContext, value: any) {
        const { handlingUnitNo, itemLength, width, height, weight, totalWeight, freightClass } = this.parent;
        const { dimensions } = this.from?.[1]?.value || {};

        // For dry van, reefer, street truck: all line item fields must be > 0
        if (isDimensionRequired) {
            return value !== null && value !== undefined && value >= 1;
        }

        if (dimensions?.length === 1) {
            const hasAnyValue = classification === 'ltl' && (handlingUnitNo >= 1 || itemLength >= 1 || width >= 1 || height >= 1 || weight >= 1 || totalWeight >= 1 || freightClass >= 1);

            if (hasAnyValue) {
                return value !== null && value !== undefined && value >= 1;
            }

            return true;
        }

        if (dimensions?.length > 1) {
            return value !== null && value !== undefined && value >= 1;
        }

        return true;
    };

    const createMaxValueValidator = (maxValueKey: keyof typeof KGS_MAX_VALUES & keyof typeof LBS_MAX_VALUES, translationKey: string) =>
        function (this: yup.TestContext, value: any) {
            const { weightMeasurement } = this.from?.[1]?.value || {};
            const maxValue = weightMeasurement === MEASUREMENT.WEIGHT2.value ? KGS_MAX_VALUES[maxValueKey] : LBS_MAX_VALUES[maxValueKey];

            if (value === null || value === undefined) return true;

            if (value > maxValue) {
                return this.createError({
                    path: `${this.path}`,
                    message: t(translationKey, { max: maxValue } as any),
                });
            }

            return true;
        };

    const dimMaxValidator = (field: 'HEIGHT' | 'WIDTH') =>
        createDimensionMaxValidator(
            field,
            t,
            (ctx) => ({
                equipmentType: shipmentDetails?.primaryEquipmentType ?? 'V',
                equipmentTypeFullName: shipmentDetails?.primaryEquipmentTypeFullName ?? 'Dry Van',
                weightMeasurement: ctx.from?.[1]?.value?.weightMeasurement ?? '',
            }),
            'AddQuote'
        );

    const transformDimensionValue = (value: any, originalValue: any) => {
        if (originalValue === '' || originalValue === null) return null;

        if (typeof originalValue === 'string') {
            const numericValue = parseFloat(originalValue);

            return isNaN(numericValue) ? null : numericValue;
        }

        return value;
    };

    const schema = yup.object().shape({
        dimensions: yup
            .array()
            .min(isDimensionRequired ? 1 : 0, 'At least one line item is required')
            .of(
                yup.object().shape({
                    handlingUnit: yup
                        .number()
                        .transform((value, originalValue) => (originalValue === '' ? null : value))
                        .nullable(),

                    handlingUnitNo: yup
                        .number()
                        .transform((value, originalValue) => (originalValue === '' ? null : value))
                        .nullable()
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    itemLength: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .max(9999)
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    width: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .test('max', '', dimMaxValidator('WIDTH'))
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    height: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .test('max', '', dimMaxValidator('HEIGHT'))
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    weight: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    totalWeight: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .test('max', '', createMaxValueValidator('MAX_WEIGHT', 'ValidationMessages.Total_weight_must_not_exceed'))
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    freightClass: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),
                    commodity: yup
                        .string()
                        .transform((value, originalValue) => (originalValue === '' ? null : value))
                        .nullable(),
                    isStack: yup.boolean().nullable(),
                })
            ),
        serviceId: yup.array().of(yup.number()).nullable(),
        totalWeight: yup.string().test('required-for-ftl-ltl', t('ValidationMessages.Total_weight_is_required'), function (value) {
            if (classification === QUOTE_CLASSIFICATION.FTL || classification === QUOTE_CLASSIFICATION.LTL) {
                if (value && Number(value) <= 0) {
                    return false;
                } else {
                    return Boolean(value && value.trim().length > 0);
                }
            }

            return true;
        }),
        linearFootage: yup
            .string()
            .test('required-for-ltl', t('ValidationMessages.Linear_footage_is_required'), function (value) {
                if (classification === QUOTE_CLASSIFICATION.LTL) {
                    return Boolean(value && value.trim().length > 0);
                }

                return true;
            })
            .test(
                'max',
                t('ValidationMessages.Linear_footage_must_not_exceed', {
                    max: 53,
                } as any),
                function (value) {
                    if (!value || value.trim().length === 0) return true;

                    const numericValue = parseFloat(value);

                    if (isNaN(numericValue)) return true;

                    return numericValue <= 53;
                }
            ),
    });

    const defaultValues = useMemo(
        () => ({
            serviceId: quoteData?.dimensionsDetails?.serviceId || [],
            dimensions:
                quoteData?.dimensionsDetails?.dimensions && quoteData?.dimensionsDetails?.dimensions?.length > 0
                    ? quoteData?.dimensionsDetails?.dimensions
                    : isDimensionRequired
                    ? defaultDimensions
                    : [],
            weightMeasurement: upperCase(quoteData?.dimensionsDetails?.weightMeasurement) || MEASUREMENT.WEIGHT1.value,
            lengthMeasurement: upperCase(quoteData?.dimensionsDetails?.lengthMeasurement) || MEASUREMENT.LENGTH1.value,
            additionalServices: {
                serviceId: quoteData?.dimensionsDetails?.additionalServices?.serviceId || [],
                dangerousGoods: quoteData?.dimensionsDetails?.additionalServices?.dangerousGoods || false,
                dangerGoodsType: quoteData?.dimensionsDetails?.additionalServices?.dangerGoodsType || '',
                description: quoteData?.dimensionsDetails?.additionalServices?.description || '',
                emergencyContactExt: quoteData?.dimensionsDetails?.additionalServices?.emergencyContactExt || '',
                emergencyContactName: quoteData?.dimensionsDetails?.additionalServices?.emergencyContactName || '',
                emergencyContactNumber: quoteData?.dimensionsDetails?.additionalServices?.emergencyContactNumber || '',
                goodsClass: quoteData?.dimensionsDetails?.additionalServices?.goodsClass || '',
                inbond: quoteData?.dimensionsDetails?.additionalServices?.inbond || false,
                inbondType: quoteData?.dimensionsDetails?.additionalServices?.inbondType || '',
                inbondName: quoteData?.dimensionsDetails?.additionalServices?.inbondName || '',
                inbondAddress: quoteData?.dimensionsDetails?.additionalServices?.inbondAddress || '',
                inbondContactMethod: quoteData?.dimensionsDetails?.additionalServices?.inbondContactMethod || '',
                inbondContactValue: quoteData?.dimensionsDetails?.additionalServices?.inbondContactValue || '',
                unitedNationsNumber: quoteData?.dimensionsDetails?.additionalServices?.unitedNationsNumber || '',
                emergencyEapnbr: quoteData?.dimensionsDetails?.additionalServices?.emergencyEapnbr || '',
                limitedAccess: quoteData?.dimensionsDetails?.additionalServices?.limitedAccess || false,
                limitedAccessType: quoteData?.dimensionsDetails?.additionalServices?.limitedAccessType || '',
                limitedAccessOther: quoteData?.dimensionsDetails?.additionalServices?.limitedAccessOther || '',
                packagingGroup: quoteData?.dimensionsDetails?.additionalServices?.packagingGroup || '',
                insuranceCommercialValue: quoteData?.dimensionsDetails?.additionalServices?.insuranceCommercialValue || '',
                insuranceCurrencyType: quoteData?.dimensionsDetails?.additionalServices?.insuranceCurrencyType || '',
                insuranceCommodity: quoteData?.dimensionsDetails?.additionalServices?.insuranceCommodity || '',
            },
            linearFootage: quoteData?.dimensionsDetails?.linearFootage ? String(quoteData.dimensionsDetails.linearFootage) : '',
            totalWeight: quoteData?.dimensionsDetails?.totalWeight ? String(quoteData.dimensionsDetails.totalWeight) : '',
        }),
        [quoteData?.dimensionsDetails]
    );

    const methods = useForm<IDimensionsForm>({
        resolver: yupResolver(schema) as unknown as Resolver<IDimensionsForm>,
        defaultValues,
    });

    const handleAction = useCallback((type: keyof typeof initialQuoteDetailAction, data: { [key: string]: any } | undefined = undefined) => {
        setAction(
            (old) =>
                ({
                    ...old,
                    [type]: true,
                    data: data || (old.data as { [key: string]: any } | null),
                } as typeof initialQuoteDetailAction)
        );
    }, []);

    const {
        setValue,
        watch,
        control,
        trigger,
        handleSubmit,
        getValues,
        formState: { isSubmitted },
        clearErrors,
    } = methods;

    // Linear footage fix: Initialize linearFootage from quoteData when it changes
    useEffect(() => {
        if (quoteData?.dimensionsDetails?.linearFootage) {
            const linearFootageValue = quoteData.dimensionsDetails.linearFootage ? String(quoteData.dimensionsDetails.linearFootage) : '';
            setValue('linearFootage', linearFootageValue);
        }
    }, [quoteData?.dimensionsDetails?.linearFootage, setValue]);

    useEffect(() => {
        if (!isDimensionRequired) {
            const currentDimensions = getValues('dimensions');
            const def = defaultDimensions[0];
            const allAreDefault = currentDimensions.every((dim: Record<string, any>) => Object.keys(def).every((key) => dim[key] === def[key]));

            if (allAreDefault) {
                setValue('dimensions', []);
            }
        }
    }, [isDimensionRequired]);

    // Pre-populate pickup/delivery services from shipper/consignee address book on first visit
    useEffect(() => {
        if (hasInitializedServices.current) return;
        hasInitializedServices.current = true;

        // Only auto-populate if user hasn't already saved service selections for this step
        if (!quoteData?.dimensionsDetails?.serviceId?.length) {
            // AddQuote uses shipperDetails and consigneeDetails instead of shipperConsigneeDetails
            const shipperServiceIds: number[] = (quoteData?.shipperDetails?.shipperAddress?.additionalServiceIds || []).map(Number).filter(Boolean);
            const consigneeServiceIds: number[] = (quoteData?.consigneeDetails?.consigneeAddress?.additionalServiceIds || []).map(Number).filter(Boolean);
            const combined = [...shipperServiceIds, ...consigneeServiceIds];
            const combinedIds = combined.filter((id, index) => combined.indexOf(id) === index);

            if (combinedIds.length > 0) {
                setValue('serviceId', combinedIds);
            }
        }
    }, []);

    // Task 20 (client decision) — buildStopOptions (LineItem.tsx) hard-filters a stop out of the
    // pickup/drop dropdown once its hint excludes that direction. That covers NEW selections, but
    // an item routed through a stop on a PRIOR visit — before the rep went back and changed that
    // stop's hint — could still be pointing at a now-invalid position (the option just silently
    // disappeared from its dropdown). Auto-clear it back to unset here rather than leave a stale,
    // no-longer-selectable value that only the display-fallback (LineItem.tsx's `?? 1`/`?? stopCount`)
    // was papering over. Runs on mount (the common case: hint was changed on an earlier step visit)
    // and again if `stops` changes while this step happens to still be mounted.
    useEffect(() => {
        if (stopCount <= 2) return;

        const toClear = clearInvalidStopRouting({ stops, items: watch('dimensions') || [] });

        toClear.forEach(({ index, field }) => {
            setValue(`dimensions.${index}.${field}`, null);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stops, stopCount]);

    const weightMeasurement = watch('weightMeasurement') || MEASUREMENT.WEIGHT1.value;

    // Shared payload builder for the Dimensions step. Used by onSubmit (Next) and by the
    // sidebar persist path, so a sideways jump stores exactly the shape defaultValues above
    // rehydrates from.
    const buildDimensionsPayload = useCallback(
        (data: IDimensionsForm) => {
            const dimensionsPayload = {
                weightMeasurement: data?.weightMeasurement || 'lbs',
                lengthMeasurement: data?.lengthMeasurement || 'in',
                // load insurance values
                commodity: data?.additionalServices?.insuranceCommodity || '',
                declaredValue: data?.additionalServices?.insuranceCommercialValue || '',
                declaredValueUnit: data?.additionalServices?.insuranceCurrencyType || '',
                totalWeight: data?.totalWeight || null,
                linearFootage: data?.linearFootage || null,
                // Multi-stop route (Task 8): classic FTL (stopCount===2) never renders this
                // component at all (see NewAddQuote.tsx), so `classification === 'ftl'` reaching
                // here only ever means FTL multileg — real line items are required there (see
                // isDimensionRequired above) and must NOT be zeroed out like classic FTL's
                // combined-form payload does.
                dimensions: classification === 'ftl' && stopCount <= 2 ? [] : data?.dimensions || [],
                serviceId: data?.serviceId || [],
                servicesData: data?.serviceId?.map((item: any) => services.find((val: any) => val.id === item)) || [],
                additionalServices: {
                    serviceId: data?.serviceId || [],
                    dangerousGoods: data?.additionalServices?.dangerousGoods || false,
                    dangerGoodsType: data?.additionalServices?.dangerGoodsType || '',
                    description: data?.additionalServices?.description || '',
                    emergencyContactExt: data?.additionalServices?.emergencyContactExt || '',
                    emergencyContactName: data?.additionalServices?.emergencyContactName || '',
                    emergencyContactNumber: data?.additionalServices?.emergencyContactNumber || '',
                    goodsClass: data?.additionalServices?.goodsClass || '',
                    inbond: data?.additionalServices?.inbond || false,
                    inbondType: data?.additionalServices?.inbondType || '',
                    inbondName: data?.additionalServices?.inbondName || '',
                    inbondAddress: data?.additionalServices?.inbondAddress || '',
                    inbondContactMethod: data?.additionalServices?.inbondContactMethod || '',
                    inbondContactValue: data?.additionalServices?.inbondContactValue || '',
                    unitedNationsNumber: data?.additionalServices?.unitedNationsNumber || '',
                    emergencyEapnbr: data?.additionalServices?.emergencyEapnbr || '',
                    limitedAccess: data?.additionalServices?.limitedAccess || false,
                    limitedAccessType: data?.additionalServices?.limitedAccessType || '',
                    limitedAccessOther: data?.additionalServices?.limitedAccessOther || '',
                    packagingGroup: data?.additionalServices?.packagingGroup || '',
                },
            };

            return dimensionsPayload;
        },
        [services, classification, stopCount]
    );

    const onSubmit = useCallback(
        (data: IDimensionsForm) => {
            // Multi-stop route (Task 7) — enforce per-item pick/drop routing before building the
            // payload. Only runs when stopCount > 2 (classic 2-stop quotes never render the
            // routing selectors, so there's nothing to validate). Same rules as BE Task 2's
            // validator, checked here so the rep gets an inline toast instead of a round trip.
            if (stopCount > 2) {
                const routing = validateItemRouting({ stopCount, items: data.dimensions });

                if (!routing.valid) {
                    WalToast.error(t(routing.errors[0].messageKey, routing.errors[0].n ? ({ n: routing.errors[0].n } as any) : undefined) as string);

                    return;
                }
            }

            const dimensionsPayload = buildDimensionsPayload(data);

            onSaveNext(dimensionsPayload, ADD_QUOTE_KEYS.DIMENSIONS);

            return { dimensionsDetails: dimensionsPayload };
        },
        [onSaveNext, stopCount, t, buildDimensionsPayload]
    );

    // Sidebar persist — reuses the exact payload builder onSubmit uses, but skips the multi-stop
    // item-routing validation (a half-filled form must still be storable) and never marks the
    // step complete or submits the quote.
    useRegisterStepperPersist(() => {
        onPersist(buildDimensionsPayload(methods.getValues()), ADD_QUOTE_KEYS.DIMENSIONS);
    });

    // Scroll the first invalid field into view when Next is blocked. RHF's onInvalid callback is
    // the only place the freshly-computed errors are available synchronously — a closure over
    // formState.errors would still hold the value from before validation ran.
    const onInvalid = useCallback((formErrors: any) => {
        scrollToFirstFormError(collectFormErrorPaths(formErrors));
    }, []);

    const handleNext = useCallback(async () => {
        const isValid = await trigger();

        if (!isValid) {
            return false;
        }

        const data = methods.getValues();
        let isTotalWeightOver = false;
        let isItemLengthOver = false;
        const isTailgate = data.serviceId.some((serviceId: number) => TAILGATE_SERVICE_IDS.includes(serviceId));
        const isActionTrue = action.tailgate || action.weightLinearFootageMismatch || action.weightMismatch || action.linearFootageMismatch;

        if (classification === QUOTE_CLASSIFICATION?.LTL && !isActionTrue) {
            let dimensionsToCheck = data.dimensions;
            let totalWeight = 0;

            dimensionsToCheck.forEach((dimension: any) => {
                totalWeight += Number(dimension.totalWeight || 0);
                const maxLengthOver = data?.weightMeasurement === MEASUREMENT.WEIGHT1.value ? LBS_MAX_VALUES.ITEM_LENGTH_OVER : KGS_MAX_VALUES.ITEM_LENGTH_OVER;

                if (dimension.itemLength >= maxLengthOver) {
                    isItemLengthOver = true;
                }
            });

            isTotalWeightOver = data.weightMeasurement === MEASUREMENT.WEIGHT1?.value ? totalWeight >= LBS_MAX_VALUES.TAILGATE_MAX_WEIGHT : totalWeight >= KGS_MAX_VALUES.TAILGATE_MAX_WEIGHT;
        }

        const isTailgateRequired = isTailgate && (isTotalWeightOver || isItemLengthOver) && classification === QUOTE_CLASSIFICATION?.LTL;

        const totalDimensionsWeight = data?.dimensions ? data?.dimensions.reduce((sum: any, item: any) => +sum + (+item.totalWeight || 0), 0) : 0;

        // weight and linear footage check
        const isWeightMismatch = classification === QUOTE_CLASSIFICATION?.LTL && data.totalWeight && totalDimensionsWeight && totalDimensionsWeight != +data.totalWeight && !isActionTrue;

        // primaryEquipmentType is read from shipmentDetails state, not formData — formData?.equipmentType is always undefined here, causing false mismatch for open-deck equipment.
        const totalDimensionsLinearFootageData =
            formatLinearFootage(calculateLinearFootage(data?.dimensions, shipmentDetails?.primaryEquipmentType, false, MEASUREMENT.WEIGHT1?.value, weightMeasurement as any)) || undefined;

        const formLinearFootage = data.linearFootage ? (typeof data.linearFootage === 'string' ? parseFloat(data.linearFootage.replace(/,/g, '')) || 0 : Number(data.linearFootage) || 0) : 0;

        const isLinearFootageMismatch =
            classification === QUOTE_CLASSIFICATION?.LTL && data.linearFootage && totalDimensionsLinearFootageData && totalDimensionsLinearFootageData !== formLinearFootage && !isActionTrue;

        if (isWeightMismatch && isLinearFootageMismatch) {
            handleAction('weightLinearFootageMismatch', {
                totalWeight: totalDimensionsWeight,
                linearFootage: totalDimensionsLinearFootageData,
                weightMeasurement: data.weightMeasurement,
            });

            return false;
        }

        if (isWeightMismatch) {
            handleAction('weightMismatch', {
                totalWeight: totalDimensionsWeight,
                weightMeasurement: data.weightMeasurement,
            });

            return false;
        }

        if (isLinearFootageMismatch) {
            handleAction('linearFootageMismatch', {
                linearFootage: totalDimensionsLinearFootageData,
                weightMeasurement: data.weightMeasurement,
            });

            return false;
        }

        if (isTailgateRequired && !isActionTrue) {
            handleAction('tailgate', {
                weightMeasurement: data.weightMeasurement,
            });

            return false;
        }

        handleSubmit(onSubmit)();

        return true;
    }, [trigger, getValues, action, classification, shipmentDetails, handleAction, handleSubmit, onSubmit, weightMeasurement]);

    const handleBack = useCallback(() => {
        const data = getValues();
        onBack(data, ADD_QUOTE_KEYS.DIMENSIONS);
    }, [getValues, onBack]);

    const watchServiceId = watch('serviceId');
    const watchTotalWeight = watch('totalWeight');
    const watchDimensions = watch('dimensions');

    const handleServiceChange = (e: any) => {
        let { checked, name, value } = e.target;
        value = parseInt(value);

        if (checked) {
            if (name === 'in-bond') {
                setAction({ ...action, inBond: true });
            } else if (name === 'load_insurance') {
                setAction({ ...action, loadInsurance: true });
            } else if (name === 'dangerous_goods') {
                setAction({ ...action, dangerousGoods: true });
            } else if (name === 'limited_access_delivery') {
                setAction({ ...action, limitedAccess: true });
            } else {
                setValue('serviceId', [...watchServiceId, value]);
            }
        } else {
            let filteredServices = watchServiceId.filter((item: any) => item !== value);
            setValue('serviceId', filteredServices);

            if (name === 'in-bond') {
                setValue('additionalServices.inbond', false);
                setValue('additionalServices.inbondType', '');
                setValue('additionalServices.inbondName', '');
                setValue('additionalServices.inbondAddress', '');
                setValue('additionalServices.inbondContactMethod', '');
                setValue('additionalServices.inbondContactValue', '');
            } else if (name === 'load_insurance') {
                setValue('additionalServices.insuranceCommercialValue', '');
                setValue('additionalServices.insuranceCurrencyType', CURRENCY.CAD);
                setValue('additionalServices.insuranceCommodity', '');
            } else if (name === 'dangerous_goods') {
                setValue('additionalServices.dangerousGoods', false);
                setValue('additionalServices.dangerGoodsType', '');
                setValue('additionalServices.unitedNationsNumber', '');
                setValue('additionalServices.packagingGroup', '');
                setValue('additionalServices.goodsClass', '');
                setValue('additionalServices.description', '');
                setValue('additionalServices.emergencyContactName', '');
                setValue('additionalServices.emergencyContactNumber', '');
                setValue('additionalServices.emergencyContactExt', '');
                setValue('additionalServices.emergencyEapnbr', '');
            } else if (name === 'limited_access_delivery') {
                setValue('additionalServices.limitedAccess', false);
                setValue('additionalServices.limitedAccessType', '');
                setValue('additionalServices.limitedAccessOther', '');
            }
        }
    };

    const handleEditService = (service: any) => () => {
        if (service?.slug === 'in-bond') {
            setAction({ ...action, inBond: true });
        } else if (service?.slug === 'load_insurance') {
            setAction({ ...action, loadInsurance: true });
        } else if (service?.slug === 'dangerous_goods') {
            setAction({ ...action, dangerousGoods: true });
        } else if (service?.slug === 'limited_access_delivery') {
            setAction({ ...action, limitedAccess: true });
        }
    };

    const textReplacements = [
        {
            original: 'After Hours / Weekend / Holiday Pickup',
            replacement: t('QuotingHub.Common.After_Hours_Pickup'),
        },
        {
            original: 'Pickup Appointment Required',
            replacement: t('QuotingHub.Common.Pickup_Appointment'),
        },
        {
            original: 'Delivery Appointment Required',
            replacement: t('QuotingHub.Common.Delivery_Appointment'),
        },
    ];

    const getReplacedText = (text: string) => {
        const replacementItem = textReplacements?.find((item) => item?.original === text);

        return replacementItem ? replacementItem.replacement : text;
    };

    const EDITABLE_SERVICES_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods', 'limited_access_delivery'];

    const getList = useCallback(
        (data: any, excludedServices: string[] = []) => (
            <>
                {data.map((val: any) =>
                    !excludedServices.includes(val.slug) &&
                    (val?.type !== 'OTHER' ||
                        classification === QUOTE_CLASSIFICATION.FTL ||
                        val?.slug === 'load_insurance' ||
                        (classification !== QUOTE_CLASSIFICATION.FTL && val.loadLinkTerm !== null)) ? (
                        <div className="flex w-[calc(33.33%_-_8px)]" key={`service_${val.id}`}>
                            <CheckBox
                                id={`service_${val.id}`}
                                onChangeFunc={handleServiceChange}
                                name={val.slug}
                                value={val.id}
                                checked={watchServiceId.includes(val.id) || false}
                                parentClassName="mt-px"
                            />
                            <div className="pl-2 leading-4 text-textSecondary text-xs font-medium truncate flex-1">
                                {val.slug === 'pickup_appointment_required' || val.slug === 'delivery_appointment_required' || val.slug === 'after_hours_pickup' ? (
                                    <TooltipCmp message={val.name} childrenClassName="!flex">
                                        <label htmlFor={`service_${val.id}`} className={`cursor-pointer truncate`}>
                                            {getReplacedText(val.name)}
                                        </label>
                                    </TooltipCmp>
                                ) : (
                                    <TruncateTextCmp message={val.name} refName={serviceRefName}>
                                        <p className="truncate" ref={serviceRefName}>
                                            <label htmlFor={`service_${val.id}`} className={`cursor-pointer`}>
                                                {getReplacedText(val.name)}
                                            </label>
                                        </p>
                                    </TruncateTextCmp>
                                )}
                            </div>
                            {watchServiceId.includes(val.id) && EDITABLE_SERVICES_SLUGS.includes(val.slug) ? (
                                <TooltipCmp message={t('Common.Edit')}>
                                    <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                                </TooltipCmp>
                            ) : null}
                        </div>
                    ) : (
                        ''
                    )
                )}
            </>
        ),
        [watchServiceId, classification]
    );

    const handleLimitedAccess = (data: ILimitedAccessDeliveryData) => {
        setValue('additionalServices.limitedAccess', true);
        setValue('additionalServices.limitedAccessOther', data.limitedAccessOther);
        setValue('additionalServices.limitedAccessType', data.selectedLimitedAccessType);
        const limitedAccessId = serviceTypes.DELIVERY.find((val: any) => val.slug === 'limited_access_delivery')?.id;

        if (!watchServiceId.includes(Number(limitedAccessId))) {
            setValue('serviceId', [...watchServiceId, Number(limitedAccessId)]);
        }
    };

    const handleDangerousGoods = (data: IDangerousGoodsForm) => {
        const loadInsuranceId = findServiceTypeIdBySlug(serviceTypes.OTHER, 'dangerous_goods');

        if (!watchServiceId.includes(Number(loadInsuranceId))) {
            setValue('serviceId', [...watchServiceId, Number(loadInsuranceId)]);
        }
        setValue('additionalServices.dangerousGoods', true);
        setValue('additionalServices.dangerGoodsType', data.dangerGoodsType);
        setValue('additionalServices.unitedNationsNumber', data.unitedNationsNumber);
        setValue('additionalServices.packagingGroup', data.packagingGroup);
        setValue('additionalServices.goodsClass', data.goodsClass);
        setValue('additionalServices.description', data.description);
        setValue('additionalServices.emergencyContactName', data.emergencyContactName);
        setValue('additionalServices.emergencyContactNumber', data.emergencyContactNumber);
        setValue('additionalServices.emergencyContactExt', data.emergencyContactExt);
        setValue('additionalServices.emergencyEapnbr', data.emergencyEapnbr);
        handleClose();
    };

    const handleInBond = (data: any) => {
        const inBondId = findServiceTypeIdBySlug(serviceTypes.OTHER, 'in-bond');

        if (!watchServiceId.includes(Number(inBondId))) {
            setValue('serviceId', [...watchServiceId, Number(inBondId)]);
        }
        setValue('additionalServices.inbond', true);
        setValue('additionalServices.inbondType', data.inbondType);
        setValue('additionalServices.inbondName', data.inbondName);
        setValue('additionalServices.inbondAddress', data.inbondAddress);
        setValue('additionalServices.inbondContactMethod', data.inbondContactMethod);
        setValue('additionalServices.inbondContactValue', data.inbondContactValue);
        handleClose();
    };

    const handleLoadInsurance = (data: ILoadInsuranceForm) => {
        const loadInsuranceId = findServiceTypeIdBySlug(serviceTypes.OTHER, 'load_insurance');
        setValue('serviceId', [...watchServiceId, Number(loadInsuranceId)]);
        setValue('additionalServices.insuranceCommercialValue', data.insuranceCommercialValue);
        setValue('additionalServices.insuranceCurrencyType', data.insuranceCurrencyType);
        setValue('additionalServices.insuranceCommodity', data.insuranceCommodity);
        handleClose();
    };

    const handleTailgate = (status: boolean) => () => {
        handleClose();

        if (status) {
            handleSubmit(onSubmit)();
        } else {
            handleClose();
        }
    };

    useEffect(() => {
        setHeaderChildren?.(
            <StepperAction
                handleNext={handleSubmit(handleNext, onInvalid)}
                handleBack={handleBack}
                nextButtonText={isAllPreviousStepsCompleted ? t('QuotingHub.Create_Quote') : t('Common.Next')}
                isNextButtonLoading={isLoading}
            />
        );

        return () => {
            setHeaderChildren?.(null);
        };
    }, [isAllPreviousStepsCompleted, handleSubmit, handleNext, handleBack, t, isLoading]);

    const watchLinearFootage = watch('linearFootage');

    useEffect(() => {
        if (watchTotalWeight) {
            clearErrors('totalWeight');
        }
    }, [watchTotalWeight]);

    useEffect(() => {
        if (watchLinearFootage) {
            clearErrors('linearFootage');
        }
    }, [watchLinearFootage]);

    useEffect(() => {
        // The previous isRendered.current guard skipped the first render after every remount, so when the user navigated Step (Shipment) → Step (Dimensions) with a new equipment, this calc never ran and the saved linearFootage from earlier persisted.
        //
        // Fix round 1 (Task 10 eval FAIL): this effect used to be gated `classification !== 'ftl'`,
        // which excluded FTL entirely from the continuous total-weight recompute below. But
        // `classification === 'ftl'` only ever reaches THIS component when stopCount > 2 — classic
        // 2-stop FTL uses the combined FtlLoadShipmentDetails step and never renders Dimensions at
        // all (see addQuote.const.ts's CP5 note / ADD_QUOTE_STEPS). So the exclusion silently broke
        // FTL multileg: the master totalWeight input is `disabled` here (isDimensionRequired is
        // forced true for FTL multileg, see above), so the user can never correct it by hand, and
        // the only other recompute path (`handleTotalWeightChange`'s fallback effect below, ~950)
        // only fires once while the field is empty/zero. That fallback would latch onto whatever
        // transient per-row total existed the first time a line item went non-zero mid-keystroke
        // (e.g. handlingUnitNo=8 × an unfinished "5" of "500" briefly producing totalWeight=40) and
        // then never recalculate again — reproducing the observed "40 lbs persisted for 6,000 lbs
        // of freight" defect. Dropping the `ftl` exclusion here makes FTL multileg recompute
        // totalWeight live from the line items on every change, exactly like LTL multileg already
        // does — both service types now use the same item-sum-auto-calc rule.
        //
        // Task 18 (client decision) — linearFootage's own `classification !== 'ftl'` exclusion is
        // dropped for the same reason: FTL multileg's Load Overview now shows the linearFootage
        // field too (see the JSX below), so it needs to actually get computed here — otherwise it
        // would render as a permanently-blank disabled input with nothing ever populating it.
        const hasRealDimensions = !isSameDimensions(watch('dimensions'), defaultDimensions);

        if (hasRealDimensions) {
            const dimensions = watch('dimensions');

            if (dimensions.length > 0) {
                const calculatedTotalWeight = dimensions.reduce((acc: number, curr: any) => acc + (Number(curr.totalWeight) || 0), 0);
                setValue('totalWeight', calculatedTotalWeight ? String(calculatedTotalWeight) : '');
            }

            // Calculate linearFootage based on dimensions, primaryEquipmentType, and weightMeasurement
            const calLinearFootage = formatLinearFootage(calculateLinearFootage(dimensions, shipmentDetails?.primaryEquipmentType, false, MEASUREMENT.WEIGHT1?.value, weightMeasurement as any)) || '';

            if (calLinearFootage) {
                setValue('linearFootage', calLinearFootage);
            }

            if (isSubmitted) {
                trigger('linearFootage');
                trigger('totalWeight');
            }
        }
    }, [
        shipmentDetails?.primaryEquipmentType,
        watch('weightMeasurement'),
        JSON.stringify(
            watch('dimensions')?.map((item: any) => ({
                handlingUnitNo: item.handlingUnitNo,
                width: item.width,
                height: item.height,
                itemLength: item.itemLength,
                weight: item.weight,
                totalWeight: item.totalWeight,
                isStack: item.isStack,
            }))
        ),
    ]);

    // Recalculate freight class when weightMeasurement or lengthMeasurement changes
    useEffect(() => {
        const lengthMeasurement = watch('lengthMeasurement');
        const dimensions = watch('dimensions');

        if (dimensions && dimensions.length > 0) {
            dimensions.forEach((dimension: any, index: number) => {
                const weight = dimension.weight;
                const itemLength = dimension.itemLength;
                const width = dimension.width;
                const height = dimension.height;

                if (weight && itemLength && width && height) {
                    const freightData = getCalculatedFreightClass({
                        itemLength: itemLength || 0,
                        width: width || 0,
                        height: height || 0,
                        weight: weight || 0,
                        lengthUnit: lengthMeasurement,
                        weightUnit: weightMeasurement,
                    });

                    const freightClass = freightData?.freightClass || 0;
                    const itemClassForDayross = freightData?.itemClassForDayross || 0;

                    setValue(`dimensions.${index}.freightClass`, freightClass);
                    setValue(`dimensions.${index}.itemClassForDayross`, itemClassForDayross);

                    if (isSubmitted) {
                        trigger(`dimensions.${index}.freightClass`);
                    }
                } else {
                    setValue(`dimensions.${index}.freightClass`, 0);
                }
            });
        }
    }, [
        watch('weightMeasurement'),
        watch('lengthMeasurement'),
        JSON.stringify(
            watch('dimensions')?.map((item: any) => ({
                weight: item.weight,
                itemLength: item.itemLength,
                width: item.width,
                height: item.height,
            }))
        ),
    ]);

    const handleTotalWeightChange = () => {
        const totalWeight = watch('dimensions').reduce((acc, dimension) => acc + Number(dimension.totalWeight || 0), 0);
        setValue('totalWeight', totalWeight ? String(totalWeight) : '');
    };

    useEffect(() => {
        const totalWeightNum = typeof watchTotalWeight === 'string' ? parseFloat(watchTotalWeight) || 0 : watchTotalWeight || 0;

        if ((totalWeightNum <= 0 || watchTotalWeight === undefined || watchTotalWeight === null || watchTotalWeight === '') && watchDimensions && watchDimensions.length > 0) {
            handleTotalWeightChange();
        }
    }, [
        JSON.stringify(
            watchDimensions?.map((item: any) => ({
                totalWeight: item.totalWeight,
                weight: item.weight,
                handlingUnitNo: item.handlingUnitNo,
                itemLength: item.itemLength,
                width: item.width,
                height: item.height,
            }))
        ),
    ]);

    const handleActionClose = () => () => {
        setAction(initialQuoteDetailAction);
    };

    const handleMismatch = (fields: (keyof IDimensionsForm)[]) => () => {
        if (action.data) {
            fields.forEach((field) => {
                setValue(field as any, action.data?.[field]);
            });
        }
        handleClose();
        handleSubmit(onSubmit)();
    };

    return (
        <>
            <FormProvider {...methods}>
                <div className="stepper-content-container ">
                    <div className="stepper-lable-wrap">
                        <label className="stepper-label-common">
                            {t('Common.Load_Overview')}
                            <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                        </label>
                        {/* Task 18 — FTL multileg's Load Overview now mirrors LTL's exactly (same fields,
                            same auto-calc label); classic 2-stop FTL never renders this component at
                            all, so `classification === FTL` reaching here always means FTL multileg. */}
                        {classification === QUOTE_CLASSIFICATION.LTL || (classification === QUOTE_CLASSIFICATION.FTL && stopCount > 2) ? (
                            <p className="text-textSecondary text-xs font-normal">{t('Common.Auto_Calculated')}</p>
                        ) : (
                            <></>
                        )}
                    </div>
                    <div className="flex items-start gap-x-3 flex-1">
                        <Controller
                            control={control}
                            name="totalWeight"
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <InputText
                                    inputName="totalWeight"
                                    placeholder={t('QuotingHub.Common.Enter_total_weight')}
                                    parentClassName="flex-1"
                                    value={value || ''}
                                    onChangeFunc={onChange}
                                    inputType="number"
                                    isAllowDecimal={true}
                                    required={classification !== QUOTE_CLASSIFICATION.FTL}
                                    shouldFormatNumber={true}
                                    maxLength={7}
                                    errorText={error?.message && error?.type === 'max' ? error?.message : !!error}
                                    isError={!!error?.message}
                                    autoFocus={!isDimensionRequired}
                                    disabled={isDimensionRequired}
                                    className="pr-10"
                                    icon={
                                        <span className="absolute text-[12px] font-medium right-3 top-1/2 -translate-y-1/2 text-xs text-gray500 pointer-events-none select-none">
                                            {watch('weightMeasurement')?.toLowerCase()}
                                        </span>
                                    }
                                />
                            )}
                        />
                        {/* Task 18 (client decision) — linear footage now shows for FTL multileg too,
                            same place/same field as LTL (previously LTL-only; see the auto-calc
                            effect below, which also had this same gate removed). */}
                        {(classification === QUOTE_CLASSIFICATION.LTL || (classification === QUOTE_CLASSIFICATION.FTL && stopCount > 2)) && (
                            <Controller
                                control={control}
                                name="linearFootage"
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <InputText
                                        inputName="linearFootage"
                                        placeholder={t('QuotingHub.Common.Enter_linear_footage')}
                                        parentClassName="flex-1"
                                        value={value || ''}
                                        onChangeFunc={onChange}
                                        required={classification !== QUOTE_CLASSIFICATION.FTL}
                                        shouldFormatNumber={true}
                                        maxLength={2}
                                        errorText={error?.message && error?.type === 'max' ? error?.message : !!error}
                                        isError={!!error?.message}
                                        inputType="number"
                                        className="pr-10"
                                        icon={<span className="absolute text-[12px] font-medium right-3 top-1/2 -translate-y-1/2 text-xs text-gray500 pointer-events-none select-none">ft</span>}
                                        isAllowDecimal={false}
                                        disabled={isDimensionRequired}
                                    />
                                )}
                            />
                        )}
                    </div>
                </div>
                {/* Multi-stop route (Task 8): classic FTL never renders this component (no Dimensions
                    step — see NewAddQuote.tsx), so `classification === QUOTE_CLASSIFICATION.FTL &&
                    stopCount > 2` only ever matches FTL multileg, which needs real line items
                    (isDimensionRequired forces true above). Classic LTL/FTL behavior is untouched. */}
                {classification === QUOTE_CLASSIFICATION.LTL || (classification === QUOTE_CLASSIFICATION.FTL && stopCount > 2) ? (
                    <div className="stepper-content-container w-full flex flex-col border-t border-utilityGray200">
                        <LineItem isDimensionRequired={isDimensionRequired} />
                    </div>
                ) : null}

                <div className="border-t border-utilityGray200 stepper-content-container border-b pb-5">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common flex items-center gap-x-0.5">
                            {t('Common.Other_Additional_Services')}
                            <div className={`cursor-pointer`} onClick={() => setAction({ ...action, otherService: true })}>
                                <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                            </div>
                        </label>
                    </div>
                    <div className="flex-1 space-y-5">
                        <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">{getList(serviceTypes?.OTHER || [], ['load_insurance'])}</div>
                    </div>
                </div>

                {action.otherService && (
                    <AdditionalServiceModal
                        handleClose={handleClose}
                        services={(serviceTypes?.OTHER || []).filter((s: any) => s.slug !== 'load_insurance')}
                        title={`Other ${t('Common.Additional_Services')}`}
                    />
                )}

                {action.inBond && <InBondModal handleClose={handleClose} handleInBond={handleInBond} defaultFormValues={watch('additionalServices') as IInBondForm} />}
                {action.loadInsurance && (
                    <LoadInsuranceModal handleClose={handleClose} handleLoadInsurance={handleLoadInsurance} defaultFormValues={watch('additionalServices') as ILoadInsuranceForm} />
                )}
                {action.dangerousGoods && (
                    <DangerousGoodsModal handleClose={handleClose} handleDangerousGoods={handleDangerousGoods} defaultFormValues={watch('additionalServices') as IDangerousGoodsForm} />
                )}
                {action.limitedAccess && (
                    <LimitedAccessDeliveryModal
                        handleClose={handleClose}
                        handleLimitedAccess={handleLimitedAccess}
                        defaultFormValues={watch('additionalServices') as ILimitedAccessDeliveryDataForm | null}
                    />
                )}

                {action.tailgate && (
                    <ConfirmModal
                        title={t('QuotingHub.Common.Tailgate_Over_Dimensions')}
                        description={t('QuotingHub.QuotingDashboard.tailgate_warning', {
                            length: FOOT,
                            weight: action.data && watch('weightMeasurement') === MEASUREMENT.WEIGHT1.value ? `${LBS_MAX_VALUES.TAILGATE_MAX_WEIGHT}lbs` : `${KGS_MAX_VALUES.TAILGATE_MAX_WEIGHT}kgs`,
                        })}
                        button1={t('Common.confirm_yes')}
                        button2={t('Common.Modify_Selection')}
                        handleClose={handleTailgate}
                    />
                )}

                {action.weightMismatch && <WeightMismatchModal handleActionClose={handleActionClose} handleMismatch={handleMismatch(['totalWeight'])} data={action.data} />}
                {action.linearFootageMismatch && <LinearFootageMismatchModal handleActionClose={handleActionClose} handleMismatch={handleMismatch(['linearFootage'])} data={action.data} />}
                {action.weightLinearFootageMismatch && (
                    <WeightLinearFootageMismatchModal handleActionClose={handleActionClose} handleMismatch={handleMismatch(['totalWeight', 'linearFootage'])} data={action.data} />
                )}
            </FormProvider>
        </>
    );
};

export default AddDimensions;
